import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useNavigate } from 'react-router-dom';
import { ArrowRightLeft, Download, Loader2, CheckCircle2, Clock, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Strip macrons / non-ASCII so jsPDF (helvetica) renders cleanly
const cleanText = (s) => (s || '').toString().replace(/[\u0100-\u017F\u01CD-\u01DC\u0300-\u036F]/g, (c) => {
  const map = { 'ā':'a','ē':'e','ī':'i','ō':'o','ū':'u','Ā':'A','Ē':'E','Ī':'I','Ō':'O','Ū':'U' };
  return map[c] || '';
});

const TransferHistory = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchTransfers = async () => {
    try {
      const res = await axios.get(`${API_URL}/transfers`, { headers: getAuthHeader() });
      setTransfers(res.data || []);
    } catch (err) {
      toast.error('Failed to load transfers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTransfers(); }, []);

  const togglePaid = async (transfer) => {
    const newStatus = transfer.payment_status === 'paid' ? 'unpaid' : 'paid';
    setUpdatingId(transfer.id);
    try {
      await axios.put(
        `${API_URL}/transfers/${transfer.id}/payment-status?status=${newStatus}`,
        {},
        { headers: getAuthHeader() }
      );
      setTransfers(prev => prev.map(t => t.id === transfer.id ? { ...t, payment_status: newStatus } : t));
      toast.success(`Marked ${newStatus.toUpperCase()}`);
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdatingId(null);
    }
  };

  // Re-generate the transfer invoice from stored data using WHOLESALE pricing
  const downloadInvoice = (transfer) => {
    try {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const dateStr = transfer.created_at ? new Date(transfer.created_at).toLocaleDateString('en-NZ') : '';

      // Header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text('STOCK TRANSFER INVOICE', pageWidth / 2, 18, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Transfer #${transfer.id}`, pageWidth / 2, 25, { align: 'center' });
      doc.text(dateStr, pageWidth / 2, 31, { align: 'center' });

      // From / To
      doc.setFont('helvetica', 'bold');
      doc.text('From:', 14, 42);
      doc.text('To:', 110, 42);
      doc.setFont('helvetica', 'normal');
      doc.text(cleanText('The Wellness Store - ' + (transfer.source_store || '').replace(/^./, c => c.toUpperCase())), 14, 48);
      doc.setFont('helvetica', 'bold');
      doc.text(cleanText(transfer.destination || ''), 110, 48);

      const items = transfer.items || [];
      const tableData = items.map((item, i) => [
        i + 1,
        cleanText(item.name),
        item.sku || '',
        item.quantity,
        `$${(item.buy_price || 0).toFixed(2)}`,
        `$${((item.buy_price || 0) * item.quantity).toFixed(2)}`,
      ]);

      autoTable(doc, {
        startY: 58,
        head: [['#', 'Product', 'SKU', 'Qty', 'Wholesale', 'Total']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [50, 50, 50], fontSize: 9, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8.5 },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 65 },
          2: { cellWidth: 30 },
          3: { cellWidth: 15, halign: 'center' },
          4: { cellWidth: 25, halign: 'right' },
          5: { cellWidth: 25, halign: 'right' },
        },
        margin: { left: 14, right: 14 },
      });

      const finalY = doc.lastAutoTable.finalY + 8;
      const totalQty = items.reduce((s, i) => s + i.quantity, 0);
      const totalValue = items.reduce((s, i) => s + (i.buy_price || 0) * i.quantity, 0);

      // Wholesale prices are ex GST — Royal Oak is a separate company, so GST (15%) is charged on the transfer
      const gstValue = totalValue * 0.15;
      const totalIncGst = totalValue + gstValue;
      doc.setFontSize(10);
      doc.text(`Total Items: ${totalQty}`, 14, finalY);
      doc.setFont('helvetica', 'normal');
      doc.text('Subtotal (ex GST):', pageWidth - 60, finalY);
      doc.text(`$${totalValue.toFixed(2)}`, pageWidth - 14, finalY, { align: 'right' });
      doc.text('GST 15%:', pageWidth - 60, finalY + 6);
      doc.text(`$${gstValue.toFixed(2)}`, pageWidth - 14, finalY + 6, { align: 'right' });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Total (inc GST):', pageWidth - 60, finalY + 13);
      doc.text(`$${totalIncGst.toFixed(2)}`, pageWidth - 14, finalY + 13, { align: 'right' });

      // Payment status stamp
      if (transfer.payment_status === 'paid') {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 130, 60);
        doc.setFontSize(20);
        doc.text('PAID', pageWidth - 35, finalY + 30, { align: 'center' });
        doc.setTextColor(0, 0, 0);
      }

      const sigY = finalY + 38;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(80);
      doc.line(14, sigY, 90, sigY);
      doc.text('Transferred by (sign)', 14, sigY + 5);
      doc.line(110, sigY, 196, sigY);
      doc.text('Received by (sign)', 110, sigY + 5);
      doc.line(14, sigY + 18, 90, sigY + 18);
      doc.text('Date', 14, sigY + 23);
      doc.line(110, sigY + 18, 196, sigY + 18);
      doc.text('Date', 110, sigY + 23);

      doc.save(`Transfer_${transfer.id}_Royal_Oak.pdf`);
      toast.success('Invoice downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate invoice');
    }
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const totalUnpaid = transfers
    .filter(t => t.payment_status !== 'paid')
    .reduce((sum, t) => sum + (t.items || []).reduce((s, i) => s + (i.buy_price || 0) * i.quantity, 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="transfer-history-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase flex items-center gap-2">
            <ArrowRightLeft className="w-7 h-7 text-orange-500" />
            Royal Oak Transfers
          </h1>
          <p className="text-slate-500 mt-1">External transfers — re-download the invoice and track who's paid.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate('/inventory')}
            className="bg-orange-500 hover:bg-orange-600 text-white h-12 px-5"
            data-testid="new-royal-oak-transfer"
          >
            <Plus className="w-5 h-5 mr-1" /> New Royal Oak Transfer
          </Button>
          <Card className="border-2 border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <p className="text-xs uppercase text-amber-700 font-semibold">Outstanding (inc GST)</p>
              <p className="text-2xl font-bold text-amber-700">${(totalUnpaid * 1.15).toFixed(2)}</p>
              <p className="text-[11px] text-amber-600">${totalUnpaid.toFixed(2)} ex GST</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-3 text-sm text-blue-900 flex items-start gap-2">
          <span className="font-bold">How to create a Royal Oak transfer:</span>
          <span>Click <strong>"+ New Royal Oak Transfer"</strong> → on the Inventory page, tick the products to transfer → click <strong>"Transfer to Royal Oak"</strong>. The PDF will save here automatically with wholesale pricing.</span>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold uppercase">All Transfers</CardTitle>
          <CardDescription>{transfers.length} transfer{transfers.length !== 1 ? 's' : ''} on record. Click Download to get the wholesale invoice. Click Paid/Unpaid to toggle status.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {transfers.length === 0 ? (
            <p className="p-8 text-center text-slate-500">No transfers yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Transfer #</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead className="text-center">Items</TableHead>
                  <TableHead className="text-right">Ex GST</TableHead>
                  <TableHead className="text-right">Total inc GST</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transfers.map((t) => {
                  const totalValue = (t.items || []).reduce((s, i) => s + (i.buy_price || 0) * i.quantity, 0);
                  const isPaid = t.payment_status === 'paid';
                  return (
                    <TableRow key={t.id} className={isPaid ? '' : 'bg-amber-50/30'}>
                      <TableCell className="text-sm">{t.created_at ? new Date(t.created_at).toLocaleDateString('en-NZ') : '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{t.id}</TableCell>
                      <TableCell className="capitalize">{t.source_store}</TableCell>
                      <TableCell>{t.destination}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{t.total_items || (t.items || []).length}</Badge>
                      </TableCell>
                      <TableCell className="text-right">${totalValue.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-bold" data-testid={`transfer-total-${t.id}`}>${(totalValue * 1.15).toFixed(2)}</TableCell>
                      <TableCell className="text-center">
                        {isPaid ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Paid
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border border-amber-300">
                            <Clock className="w-3 h-3 mr-1" /> Unpaid
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadInvoice(t)}
                            data-testid={`download-transfer-${t.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" /> Download
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => togglePaid(t)}
                            disabled={updatingId === t.id}
                            className={isPaid
                              ? 'bg-amber-500 hover:bg-amber-600 text-white'
                              : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                            data-testid={`toggle-paid-${t.id}`}
                          >
                            {updatingId === t.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : (isPaid ? 'Mark Unpaid' : 'Mark Paid')}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TransferHistory;
